// src/components/Sidebar/MasterSidebar.jsx
import { useState, useEffect } from "react";
import { MenuItem } from "react-pro-sidebar";
import { Box, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import ClassIcon from "@mui/icons-material/Class";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StoreIcon from "@mui/icons-material/Store";

const Item = ({ title, to, icon, selected, setSelected, style }) => {
  const theme = useTheme();
  const colors = theme.palette; // Access palette directly from theme

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.text.primary,
        ...style,
        backgroundColor:
          selected === title ? colors.primary.main : "transparent",
        borderRadius: "10px",
      }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Typography
        style={{
          fontWeight: "bold",
          color: style?.color || colors.text.primary,
          fontSize: "16px",
        }}
      >
        {title}
      </Typography>
      <Link to={to} />
    </MenuItem>
  );
};

export default function MasterSidebar({ isCollapsed, selected, setSelected }) {
  const theme = useTheme();
  const colors = theme.palette; // Access palette directly from theme
  const location = useLocation();
  
  const [isMarketPlaceOpen, setIsMarketPlaceOpen] = useState(() => {
    const saved = localStorage.getItem("masterSidebarOpen");
    return saved ? JSON.parse(saved) : false;
  });

  // Save sidebar open state to localStorage
  useEffect(() => {
    localStorage.setItem("masterSidebarOpen", JSON.stringify(isMarketPlaceOpen));
  }, [isMarketPlaceOpen]);

  // Auto-expand sidebar if current route is in masters section
  useEffect(() => {
    const masterRoutes = [
      "/Infinite/Ledger",
      "/Infinite/LedGroup",
      "/Infinite/VoucherType"
    ];
    
    if (masterRoutes.includes(location.pathname) && !isMarketPlaceOpen) {
      setIsMarketPlaceOpen(true);
    }
  }, [location.pathname, isMarketPlaceOpen]);

  const handleMarketToggle = () => {
    setIsMarketPlaceOpen(!isMarketPlaceOpen);
  };

  return (
    <Box
      sx={{
        // Custom scrollbar for nested content if needed
        "& .pro-inner-list-item": {
          "&::-webkit-scrollbar": {
            width: "1px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            borderRadius: "1px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: colors.primary.main,
            borderRadius: "1px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: colors.primary.dark,
          },
          scrollbarWidth: "thin",
          scrollbarColor: `${colors.primary.main} transparent`,
        },
      }}
    >
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={handleMarketToggle}
      >
        <StoreIcon
          fontSize="small"
          style={{
            marginLeft: "25px",
            color: colors.text.primary,
          }}
        />
        <Typography
          paddingLeft={isCollapsed ? "25%" : "0px"}
          color={colors.text.primary}
          sx={{
            m: "10px 0 5px 15px",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "16px",
          }}
        >
          Masters
        </Typography>
        {isMarketPlaceOpen ? (
          <ExpandLessIcon
            style={{
              marginLeft: "auto",
              color: colors.text.primary,
            }}
          />
        ) : (
          <ExpandMoreIcon
            style={{
              marginLeft: "auto",
              color: colors.text.primary,
            }}
          />
        )}
      </Box>

      {isMarketPlaceOpen && (
        <Box
          style={{
            marginLeft: "20px",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Item
            title="Ledger"
            to="/Infinite/Ledger"
            icon={<PeopleOutlinedIcon fontSize="small" />}
            selected={selected}
            setSelected={setSelected}
            style={{ color: colors.text.secondary }}
          />
          <Item
            title="Groups"
            to="/Infinite/LedGroup"
            icon={<GroupsIcon fontSize="small" />}
            selected={selected}
            setSelected={setSelected}
            style={{ color: colors.text.secondary }}
          />
          <Item
            title="Voucher Type"
            to="/Infinite/VoucherType"
            icon={<ClassIcon fontSize="small" />}
            selected={selected}
            setSelected={setSelected}
            style={{ color: colors.text.secondary }}
          />
        </Box>
      )}
    </Box>
  );
}